// Перестановка чипов мышью и клавиатурой. Хук отдаёт пропсы для каждого чипа: pointerdown на самом чипе
// (не на его кнопке ×) после порога — протяжка; Alt+←/→ на сфокусированном чипе — шаг. Место приземления —
// контур-призрак размером с чип (ghost), как у задач на сетке: ряд отдаёт его перед чипом ghost.before или в конец.
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from './cn';
import { chipAnchor, dropChip, moveChip } from './chipOrder';

const THRESHOLD = 4;

export type ChipProps = {
  'data-chip-id': string;
  tabIndex: number;
  className: string;
  title: string;
  onPointerDown: (e: PointerEvent<HTMLElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
};

/** Куда ляжет чип: перед before (null — в конец); нет, пока не тащат или позиция не меняется. */
export type ChipGhost = { id: string; before: string | null };

export function useChipReorder(ids: readonly string[], onReorder: (ids: string[]) => void): { chipProps: (id: string) => ChipProps; dragging: string | null; ghost: ChipGhost | null } {
  const [drag, setDrag] = useState<{ id: string; anchor: string | null } | null>(null);
  const press = useRef<{ id: string; x: number; y: number } | null>(null);
  const dragRef = useRef(drag);
  dragRef.current = drag;

  // слушатели окна живут пока смонтирован ряд: нажатие хранится в ref и ререндера не требует
  useEffect(() => {
    const onMove = (e: globalThis.PointerEvent) => {
      const p = press.current;
      if (!p) return;
      if (!dragRef.current) {
        if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < THRESHOLD) return;
        setDrag({ id: p.id, anchor: p.id });
      }
      const over = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest<HTMLElement>('[data-chip-id]');
      if (!over) return;
      const overId = over.dataset.chipId!;
      if (!ids.includes(overId)) return;
      const anchor = chipAnchor(ids, overId, e.clientX, over.getBoundingClientRect());
      setDrag((d) => (d ? { ...d, anchor } : d));
    };
    const onUp = () => {
      const d = dragRef.current;
      press.current = null;
      if (d) {
        const next = dropChip(ids, d.id, d.anchor);
        if (next.join() !== ids.join()) onReorder(next);
        setDrag(null);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onUp); };
  }, [ids, onReorder]);

  const chipProps = useCallback((id: string): ChipProps => {
    const isDragged = drag?.id === id;
    return {
      'data-chip-id': id,
      tabIndex: 0,
      title: 'Drag or Alt+←/→ to reorder',
      className: cn('cursor-grab select-none outline-none focus-visible:ring-2 focus-visible:ring-accent', isDragged && 'opacity-40 cursor-grabbing'),
      onPointerDown: (e) => {
        if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
        press.current = { id, x: e.clientX, y: e.clientY };
      },
      onKeyDown: (e) => {
        if (!e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
        e.preventDefault();
        const next = moveChip(ids, id, e.key === 'ArrowLeft' ? -1 : 1);
        if (next.join() === ids.join()) return;
        onReorder(next);
        requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-chip-id="${id}"]`)?.focus());
      },
    };
  }, [drag, ids, onReorder]);

  // призрак не показываем там, где бросок ничего не меняет: своё место и место сразу за собой
  const ghost = drag && dropChip(ids, drag.id, drag.anchor).join() !== ids.join() ? { id: drag.id, before: drag.anchor } : null;
  return { chipProps, dragging: drag?.id ?? null, ghost };
}

/** Контур чипа на месте приземления; className — размеры того ряда, где он стоит. */
export function ChipGhostMark({ label, className }: { label: string; className?: string }) {
  return <span aria-hidden data-chip-ghost className={cn('inline-flex items-center border-2 border-dashed border-accent text-accent/80 bg-accent/5 rounded-full px-2', className)}>{label}</span>;
}
